import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { isFuture, isPast } from 'date-fns';
import type { CSSProperties, ReactNode } from 'react';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type { To } from 'react-router';
import { Outlet } from 'react-router';
import { useMeasure } from 'react-use';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import {
  Sport,
  UserGroupStatus,
} from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { FootballLogo } from '@sorare/core/src/atoms/icons/FootballLogo';
import { NBABall } from '@sorare/core/src/atoms/icons/NBABall';
import { iconCalendar } from '@sorare/core/src/atoms/icons/system/iconCalendar.ts';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { ListWithDetailLayoutWrapper } from '@sorare/core/src/components/layout/ListWithDetailLayout';
import { DateRange } from '@sorare/core/src/components/lobby/DateRange';
import { FixtureStatus } from '@sorare/core/src/components/lobby/FixtureStatus';
import { OverlappingAvatars } from '@sorare/core/src/components/rivals/OverlappingAvatars';
import { ANY_SPORT_PLAY_EVENTTYPE_CHAMPIONSHIP_LEADERBOARD_USERGROUP } from '@sorare/core/src/constants/__generated__/routes';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { FixtureState } from '@sorare/core/src/lib/so5';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { regularMaterial } from '@sorare/core/src/style/utils';

import { ChampionshipCompetitionSelect } from '#src/components/championship/ChampionshipCompetitionSelect/index.tsx';
import { ChampionshipRaritySelect } from '#src/components/championship/ChampionshipRaritySelect/index.tsx';
import { CumulativeRankRewards } from '#src/components/leaderboard/CumulativeRankRewards/index.tsx';

import type {
  ChampionshipLayout_so5UserGroup,
  ChampionshipLayout_so5UserGroupLeaderboard,
  GetAllUserGroupsQuery,
  GetAllUserGroupsQueryVariables,
} from './__generated__/index.graphql.ts';

const Aside = styled(Vertical)`
  --selects-height: calc(8 * var(--unit));

  position: relative;
  isolation: isolate;

  background-size: 100% 200px;
  background-image:
    linear-gradient(transparent, var(--c-black)), var(--header-background-url);
  background-repeat: no-repeat;

  @media ${desktopAndAbove} {
    gap: var(--triple-unit);
    background: initial;
    max-height: calc(100vh - var(--current-stack-height));
  }
`;
const Top = styled.div`
  position: relative;
  margin-top: calc((var(--selects-height) + var(--triple-unit)) * -1);

  @media ${desktopAndAbove} {
    margin: 0;
  }
`;
const Picture = styled.img`
  width: 100%;
`;
const Selects = styled(FullWidthContainer)`
  position: sticky;
  top: var(--current-stack-height);
  z-index: 1;
  isolation: isolate;
  min-height: var(--selects-height);
  display: flex;
  gap: var(--unit);
  align-items: center;
  max-width: 100%;

  @media ${desktopAndAbove} {
    top: 0;
    margin-top: calc((var(--selects-height) + var(--triple-unit)) * -1);
  }

  .scrolledFurtherThanImage & {
    ${regularMaterial}
  }
`;
const FixtureStatusWrapper = styled.div`
  position: absolute;
  top: var(--double-unit);
  right: var(--double-unit);
`;

const ALL_USERGROUPS_QUERY = gql`
  query GetAllUserGroupsQuery($sport: Sport) {
    so5 {
      universalSo5UserGroups(sport: $sport) {
        nodes {
          slug
          ...ChampionshipRaritySelect_so5UserGroup
          ...ChampionshipCompetitionSelect_so5UserGroup
        }
      }
    }
  }
  ${ChampionshipRaritySelect.fragments.so5UserGroup}
  ${ChampionshipCompetitionSelect.fragments.so5UserGroup}
` as TypedDocumentNode<GetAllUserGroupsQuery, GetAllUserGroupsQueryVariables>;

const sportIcons = {
  [Sport.FOOTBALL]: <FootballLogo />,
  [Sport.NBA]: <NBABall color="var(--c-yellow-500)" />,
  [Sport.BASEBALL]: null,
};

type Props = {
  so5UserGroup: ChampionshipLayout_so5UserGroup;
  to: ({ userGroup, user }: { userGroup: string; user: string }) => To;
  additionalContent?: ReactNode;
};
export const ChampionshipLayout = ({
  so5UserGroup,
  to,
  additionalContent,
}: Props) => {
  const { formatMessage } = useIntlContext();
  const { sport, generateSportContextPath } = useSportContext();

  const { data } = useQuery(ALL_USERGROUPS_QUERY, {
    variables: { sport },
  });
  const allUserGroups = data?.so5.universalSo5UserGroups.nodes || [];

  const nextFixtureForTimeLeft =
    so5UserGroup?.upcomingSo5Leaderboard?.so5Fixture ||
    so5UserGroup?.endSo5Fixture;

  const isDesktopAndAbove = useIsDesktopAndAbove();

  const [topRef, { height: topHeight }] = useMeasure<HTMLDivElement>();
  const [selectsRef, { height: selectsHeight }] = useMeasure<HTMLDivElement>();
  const ref = useRef<HTMLDivElement>(null);
  const [scrolledFurtherThanImage, setScrolledFurtherThanImage] =
    useState(false);

  const onScrollMobile = useCallback(() => {
    if (isDesktopAndAbove) {
      return;
    }

    if (selectsHeight) {
      setScrolledFurtherThanImage(window.scrollY >= selectsHeight);
    }
  }, [isDesktopAndAbove, selectsHeight]);

  useEffect(() => {
    window.addEventListener('scroll', onScrollMobile);

    return () => {
      window.removeEventListener('scroll', onScrollMobile);
    };
  }, [onScrollMobile]);

  const competitionGroup =
    so5UserGroup?.latestSo5Leaderboard?.competitionGroup ||
    so5UserGroup?.upcomingSo5Leaderboard?.competitionGroup;
  const pictureUrl = so5UserGroup?.pictureUrl || competitionGroup?.pictureUrl;
  const color =
    competitionGroup?.optionalColor ||
    competitionGroup?.secondaryColor ||
    competitionGroup?.primaryColor;

  const chaseStartDate = so5UserGroup?.startSo5Fixture?.startDate;
  const chaseEndDate =
    so5UserGroup?.endSo5Fixture?.endDate || so5UserGroup?.customEndDate;

  const isLive =
    chaseStartDate &&
    chaseEndDate &&
    isPast(chaseStartDate) &&
    isFuture(chaseEndDate);

  return (
    <Suspense fallback={<LoadingIndicator fullHeight />}>
      <ListWithDetailLayoutWrapper
        style={
          {
            '--color': color,
          } as CSSProperties
        }
      >
        <Aside
          ref={ref}
          onScroll={() => {
            if (isDesktopAndAbove && topHeight && ref.current) {
              setScrolledFurtherThanImage(ref.current.scrollTop >= topHeight);
            }
          }}
          className={classNames({
            scrolledFurtherThanImage,
          })}
        >
          {!!allUserGroups.length && (
            <Selects ref={selectsRef}>
              <>
                <ChampionshipRaritySelect
                  userGroups={allUserGroups}
                  selectUserGroupSlug={so5UserGroup?.slug || ''}
                  to={params => to(params)}
                />
                <ChampionshipCompetitionSelect
                  userGroups={allUserGroups}
                  selectUserGroupSlug={so5UserGroup?.slug || ''}
                  to={params => to(params)}
                />
              </>
            </Selects>
          )}
          <Top ref={topRef}>
            {!!pictureUrl && <Picture src={pictureUrl} alt="" />}
          </Top>
          <FullWidthContainer>
            <Vertical gap={3}>
              <Horizontal>
                <Skeleton loading={!so5UserGroup}>
                  <Horizontal>
                    <FontAwesomeIcon icon={iconCalendar} width={12} />
                    <LabelM color="var(--c-nd-600)">
                      {!!chaseStartDate && !!chaseEndDate && (
                        <DateRange
                          startDate={chaseStartDate}
                          endDate={chaseEndDate}
                        />
                      )}
                    </LabelM>
                  </Horizontal>
                </Skeleton>
                {isLive ? (
                  <FixtureStatusWrapper>
                    <FixtureStatus fixtureState={FixtureState.started} />
                  </FixtureStatusWrapper>
                ) : (
                  <>
                    {!!chaseStartDate &&
                      !!chaseEndDate &&
                      !!nextFixtureForTimeLeft && (
                        <LabelM color="var(--c-yellow-400)">
                          <TimeLeft
                            endLabel={
                              so5UserGroup?.endSo5Fixture?.slug ===
                              nextFixtureForTimeLeft.slug
                                ? formatMessage(fantasy.ended)
                                : undefined
                            }
                            time={chaseEndDate}
                          />
                        </LabelM>
                      )}
                  </>
                )}
              </Horizontal>
              {!!so5UserGroup?.description && (
                <Horizontal>
                  <LabelM>{so5UserGroup.description}</LabelM>
                </Horizontal>
              )}
              <Horizontal>
                {so5UserGroup?.membershipAvatars.nodes.length > 0 && (
                  <OverlappingAvatars
                    avatars={so5UserGroup.membershipAvatars.nodes.map(
                      ({ user }) => ({
                        slug: user.slug,
                        avatarUrl: user.pictureUrl,
                      })
                    )}
                    additionalPlayersCount={0}
                    size={4}
                  />
                )}
                <Skeleton loading={!so5UserGroup}>
                  <LabelM color="var(--c-nd-600)">
                    <FormattedMessage
                      defaultMessage="{count, plural, one {# player} other {# players}}"
                      values={{
                        count: so5UserGroup?.membershipsCount || 0,
                      }}
                    />
                  </LabelM>
                </Skeleton>
              </Horizontal>

              <Vertical>
                <Horizontal>
                  {sportIcons[sport]}
                  <LabelL bold>
                    <FormattedMessage defaultMessage="Scoring" />
                  </LabelL>
                </Horizontal>
                <LabelM color="var(--c-nd-600)">
                  <FormattedMessage
                    defaultMessage="The {count} best lineups will be your total score"
                    values={{ count: so5UserGroup?.topScoresCount }}
                  />
                </LabelM>
              </Vertical>

              <Vertical>
                <SBHorizontal gap={1}>
                  <Horizontal>
                    <FontAwesomeIcon icon={iconCalendar} width={12} />
                    <LabelL bold>
                      <FormattedMessage {...fantasy.leaderboard} />
                    </LabelL>
                  </Horizontal>
                  {sport !== Sport.BASEBALL && (
                    <Link
                      to={generateSportContextPath(
                        ANY_SPORT_PLAY_EVENTTYPE_CHAMPIONSHIP_LEADERBOARD_USERGROUP,
                        {
                          params: {
                            eventType: 'classic',
                            userGroup: so5UserGroup?.slug || '',
                          },
                        }
                      )}
                    >
                      <LabelM bold color="var(--c-blue-300)">
                        <FormattedMessage defaultMessage="View Full" />
                      </LabelM>
                    </Link>
                  )}
                </SBHorizontal>
                <CumulativeRankRewards
                  doNotDisplayUsers={
                    so5UserGroup.status === UserGroupStatus.TO_START
                  }
                  userGroupSlug={so5UserGroup?.slug || ''}
                  rankingLink={(rankingFixture, _userGroupSlug, rankingUser) =>
                    to({
                      userGroup: so5UserGroup?.slug || '',
                      user: rankingUser,
                    }).toString()
                  }
                />
              </Vertical>
              {additionalContent}
            </Vertical>
          </FullWidthContainer>
        </Aside>
        <Outlet
          context={{
            so5UserGroup,
            layoutLoading: !so5UserGroup,
          }}
        />
      </ListWithDetailLayoutWrapper>
    </Suspense>
  );
};

const so5UserGroupLeaderboardFragment = gql`
  fragment ChampionshipLayout_so5UserGroupLeaderboard on So5Leaderboard {
    slug
    so5Fixture {
      slug
    }
    competitionGroup: so5LeaderboardGroup(groupType: COMPETITION) {
      slug
      pictureUrl
      primaryColor: color(variant: PRIMARY)
      secondaryColor: color(variant: SECONDARY)
      optionalColor: color(variant: OPTIONAL)
    }
  }
` as TypedDocumentNode<ChampionshipLayout_so5UserGroupLeaderboard>;

ChampionshipLayout.fragments = {
  so5UserGroup: gql`
    fragment ChampionshipLayout_so5UserGroup on So5UserGroup {
      slug
      status
      description
      topScoresCount
      customEndDate
      membershipsCount
      pictureUrl
      membershipAvatars: membershipsPaginated(pageSize: 3) {
        nodes {
          id
          user {
            slug
            pictureUrl(derivative: "avatar")
          }
        }
      }
      startSo5Fixture {
        slug
        startDate
      }
      endSo5Fixture {
        slug
        endDate
      }
      upcomingSo5Leaderboard {
        slug
        ...ChampionshipLayout_so5UserGroupLeaderboard
      }
      latestSo5Leaderboard {
        slug
        ...ChampionshipLayout_so5UserGroupLeaderboard
      }
    }
    ${so5UserGroupLeaderboardFragment}
  ` as TypedDocumentNode<ChampionshipLayout_so5UserGroup>,
};
